import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface TimeLeft {
  horas: string;
  minutos: string;
  segundos: string;
}

function calcularTempoRestante(): TimeLeft {
  const agora = new Date();
  const meiaNiote = new Date();
  meiaNiote.setHours(24, 0, 0, 0);
  const diff = Math.max(0, meiaNiote.getTime() - agora.getTime());

  const totalSegundos = Math.floor(diff / 1000);
  const horas = Math.floor(totalSegundos / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = totalSegundos % 60;

  return {
    horas: String(horas).padStart(2, '0'),
    minutos: String(minutos).padStart(2, '0'),
    segundos: String(segundos).padStart(2, '0'),
  };
}

// Vagas disponíveis: número pseudo-aleatório mas estável por sessão
function getVagas(): number {
  const seed = new Date().toDateString();
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return 5 + (Math.abs(hash) % 8); // entre 5 e 12
}

const VAGAS_TOTAL = 20;

interface OfertaPlanosProps {
  onCheckoutClick?: () => void;
}

export default function OfertaPlanos({ onCheckoutClick }: OfertaPlanosProps) {
  const [tempo, setTempo] = useState<TimeLeft>(calcularTempoRestante());
  const [vagas] = useState<number>(getVagas());
  const [pulsar, setPulsar] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTempo(calcularTempoRestante());
      setPulsar(true);
      setTimeout(() => setPulsar(false), 300);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const percentualOcupado = Math.round(((VAGAS_TOTAL - vagas) / VAGAS_TOTAL) * 100);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-gold-500/40 bg-gradient-to-br from-charcoal-900 via-[#1a1610] to-charcoal-900">
      {/* Glow superior */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />
      {/* Glow de fundo */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(201,164,64,0.08)_0%,_transparent_60%)] pointer-events-none" />

      <div className="relative p-5">

        {/* Badge topo */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gold-500/15 border border-gold-500/35 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />
            <span className="text-[10px] font-bold text-gold-400 tracking-[0.2em] uppercase">Oferta especial</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-white/35 font-medium">
            <i className="ri-time-line text-gold-500/50" />
            expira hoje
          </div>
        </div>

        {/* Título da oferta */}
        <h3 className="font-display text-lg font-bold text-white leading-snug mb-1">
          Acesso Completo ao Método
        </h3>
        <p className="text-xs text-white/45 leading-relaxed mb-4">
          Todos os exercícios completos, áudios guiados, workbooks em PDF e acompanhamento — com desconto exclusivo para seguidoras do Instagram.
        </p>

        {/* Preço */}
        <div className="flex items-end gap-3 mb-4">
          <div>
            <p className="text-[11px] text-white/30 line-through font-medium mb-0.5">De R$ 297</p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-[11px] text-gold-400 font-semibold">Por apenas</span>
              <span className="font-display text-3xl font-bold text-gold-300 leading-none">R$ 97</span>
              <span className="text-[11px] text-white/40">/mês</span>
            </div>
          </div>
          <div className="ml-auto flex flex-col items-end gap-1">
            <span className="text-[11px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full whitespace-nowrap">
              67% OFF
            </span>
            <span className="text-[10px] text-white/25">garantia 7 dias</span>
          </div>
        </div>

        {/* Contador regressivo */}
        <div className="bg-black/30 border border-gold-500/20 rounded-xl p-3.5 mb-4">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-[10px] text-white/35 font-semibold uppercase tracking-wider">
              Desconto expira em
            </span>
            <i className="ri-alarm-warning-line text-gold-500/50 text-xs" />
          </div>
          <div className="flex items-center justify-center gap-2">
            {/* Horas */}
            <div className="flex flex-col items-center">
              <div className={`w-14 h-12 flex items-center justify-center rounded-lg bg-gold-500/10 border border-gold-500/25 transition-all duration-150 ${pulsar ? 'bg-gold-500/18' : ''}`}>
                <span className="font-display text-2xl font-bold text-gold-300 leading-none tabular-nums">
                  {tempo.horas}
                </span>
              </div>
              <span className="text-[9px] text-white/25 mt-1 tracking-wider uppercase">horas</span>
            </div>

            <span className="text-gold-500/50 text-xl font-bold mb-3 tabular-nums">:</span>

            {/* Minutos */}
            <div className="flex flex-col items-center">
              <div className={`w-14 h-12 flex items-center justify-center rounded-lg bg-gold-500/10 border border-gold-500/25 transition-all duration-150 ${pulsar ? 'bg-gold-500/18' : ''}`}>
                <span className="font-display text-2xl font-bold text-gold-300 leading-none tabular-nums">
                  {tempo.minutos}
                </span>
              </div>
              <span className="text-[9px] text-white/25 mt-1 tracking-wider uppercase">min</span>
            </div>

            <span className="text-gold-500/50 text-xl font-bold mb-3 tabular-nums">:</span>

            {/* Segundos */}
            <div className="flex flex-col items-center">
              <div className={`w-14 h-12 flex items-center justify-center rounded-lg bg-gold-500/10 border border-gold-500/25 transition-all duration-150 ${pulsar ? 'bg-gold-500/18 border-gold-400/40' : ''}`}>
                <span className="font-display text-2xl font-bold text-gold-300 leading-none tabular-nums">
                  {tempo.segundos}
                </span>
              </div>
              <span className="text-[9px] text-white/25 mt-1 tracking-wider uppercase">seg</span>
            </div>
          </div>
        </div>

        {/* Vagas disponíveis */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <i className="ri-user-line text-white/30 text-xs" />
              <span className="text-[11px] text-white/45 font-medium">
                Vagas disponíveis hoje
              </span>
            </div>
            <span className="text-[11px] font-bold text-gold-400">
              {vagas} de {VAGAS_TOTAL}
            </span>
          </div>
          {/* Barra de progresso */}
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-gold-600 to-gold-400 rounded-full transition-all duration-700"
              style={{ width: `${percentualOcupado}%` }}
            />
          </div>
          <p className="text-[10px] text-white/25 mt-1.5">
            {percentualOcupado}% das vagas já preenchidas hoje
          </p>
        </div>

        {/* O que está incluído */}
        <div className="space-y-1.5 mb-4">
          {[
            { icon: 'ri-video-line', text: 'Exercícios completos com áudio guiado' },
            { icon: 'ri-file-text-line', text: 'Workbooks em PDF para cada técnica' },
            { icon: 'ri-customer-service-2-line', text: 'Suporte direto com a Bia' },
            { icon: 'ri-infinity-line', text: 'Acesso vitalício ao conteúdo' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <i className={`${item.icon} text-gold-500/60 text-xs`} />
              <span className="text-xs text-white/40 leading-tight">{item.text}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          to="/planos"
          onClick={onCheckoutClick}
          className="group flex items-center justify-center gap-2.5 w-full py-4 rounded-xl bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-700 hover:to-gold-600 text-charcoal-900 font-bold text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-vip-crown-line text-base" />
          Garantir minha vaga com desconto
          <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform" />
        </Link>

        {/* Garantia */}
        <div className="flex items-center justify-center gap-1.5 mt-3">
          <i className="ri-shield-check-line text-white/25 text-xs" />
          <span className="text-[10px] text-white/25">Garantia de 7 dias — satisfação total ou devolução</span>
        </div>
      </div>

      {/* Glow inferior */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />
    </div>
  );
}
